import type { ServiceResponse, ApiErrorResponse, UUID } from '@dopomogai/types';
import type { UploadedFileResponse, FilePurposeEnum } from '@dopomogai/types';
import { getApiClient, getRawApiBaseUrl } from '../client';
import { handleApiError } from '../utils/handleApiError';

/**
 * Widget-scoped file API (visitor uploads).
 * Uses axios instance so:
 *  - auth headers come from setAuthHeaderProvider()
 *  - 401 refresh queue works (setAuthRefresh)
 *
 * Backend routes expected:
 *  - POST /api/v1/widgets/{widget_version_id}/files/upload
 *  - GET  /api/v1/widgets/{widget_version_id}/files/{file_id}
 *  - POST /api/v1/widgets/{widget_version_id}/files/metadata/batch
 *  - GET  /api/v1/widgets/{widget_version_id}/files/{file_id}/download
 */
export const widgetFilesService = {
  upload: async (
    widgetVersionId: UUID,
    file: File,
    opts?: {
      purpose?: FilePurposeEnum;
      agentId?: UUID;
      sha256?: string;
    }
  ): ServiceResponse<UploadedFileResponse> => {
    try {
      if (!widgetVersionId) {
        const err: ApiErrorResponse = { isError: true, message: 'widgetVersionId is required', status: 400 } as any;
        return err;
      }
      if (!file) {
        const err: ApiErrorResponse = { isError: true, message: 'file is required', status: 400 } as any;
        return err;
      }

      const fd = new FormData();
      if (opts?.purpose) fd.append('purpose', String(opts.purpose));
      if (opts?.sha256) fd.append('sha256', String(opts.sha256));
      fd.append('file', file, file.name);

      const res = await getApiClient()
        .post<UploadedFileResponse>(
          `/widgets/${String(widgetVersionId)}/files/upload`,
          fd,
          {
            params: opts?.agentId ? { agent_id: String(opts.agentId) } : undefined,
            headers: {
              // Let axios set the proper boundary; just ensure we don't force JSON
              'Content-Type': 'multipart/form-data',
            },
          }
        )
        .then((r) => r.data);

      return res;
    } catch (err) {
      return handleApiError(err, 'uploading widget file');
    }
  },

  getMetadata: async (widgetVersionId: UUID, fileId: UUID): ServiceResponse<UploadedFileResponse> => {
    try {
      const res = await getApiClient()
        .get<UploadedFileResponse>(`/widgets/${String(widgetVersionId)}/files/${String(fileId)}`)
        .then((r) => r.data);
      return res;
    } catch (err) {
      return handleApiError(err, 'fetching widget file metadata');
    }
  },

  getMetadataBatch: async (
    widgetVersionId: UUID,
    fileIds: UUID[]
  ): ServiceResponse<{ items: UploadedFileResponse[] }> => {
    try {
      const res = await getApiClient()
        .post<{ items: UploadedFileResponse[] }>(
          `/widgets/${String(widgetVersionId)}/files/metadata/batch`,
          fileIds || [],
          { headers: { 'Content-Type': 'application/json' } }
        )
        .then((r) => r.data);
      return res;
    } catch (err) {
      return handleApiError(err, 'fetching widget file metadata batch');
    }
  },

  getDownloadUrl: (widgetVersionId: UUID, fileId: UUID): string => {
    const base = getRawApiBaseUrl();
    return `${base}/api/v1/widgets/${encodeURIComponent(String(widgetVersionId))}/files/${encodeURIComponent(
      String(fileId)
    )}/download`;
  },
};